import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { Form, FormRenderProps, FormSpy } from 'react-final-form';
import { useApolloClient } from '@apollo/client';
import dynamic from 'next/dynamic';
import { NewSearchCategories } from './NewSearchCategories';
import {
  convertSearchValues,
  convertValues,
  fromObjToUrl
} from '../../utils/helpers';
import {
  setChosenLocationNames,
  setCurrentCity,
  setCurrentState,
  setLocationIds,
  setCategoryIds,
  setResetButtonIsDisabled,
  setAdriverSelectedCity
} from '../../actions';
import { SearchTypeHeader } from './SearchTypeHeader';
import {
  ANY_CITY_ID,
  BUILDING_TYPE_ID,
  Cities,
  urlHashes
} from '../../utils/helpers/constants';
import { FilterSlugs, LeasedSlugs } from '../../utils/helpers/slugFilters';
import { NewSearchProps } from './types';
import {
  selectSearchFormCurrentCity,
  selectSearchFormCurrentSortType,
  selectSearchFormCurrentState,
  selectSearchFormLocationIds,
  selectSideMenuModalState
} from '../../store/selectors';
import { CategoriesContainer, SearchBarForm, SearchContainer } from './styled';
import useScreenSize from '../../hooks/useScreenSize';
import { useClient } from '../../hooks/useClient';
import { useGoBack } from '../../hooks/useGoBack';
import { RentalTabs } from './RentalTabs';
import { NewSearchBarInitialComponent } from './NewSearchBar/NewSearchBarInitialComponent';
import { getLocalDataQuery } from '../../api/queries';
import { LocalDataQuery } from '../../graphql/graphql';
import { AllItemsLink } from './AllItemsLink';
import { getSearchLandingPages } from './searchLandingPages';
import { SearchFormValues } from '../../utils/helpers/search';

const NewSearchBar = dynamic(
  () => import('./NewSearchBar').then(mod => mod.NewSearchBar),
  {
    ssr: false,
    loading: NewSearchBarInitialComponent
  }
);

const getInitialSearchType = (url: string, leased?: string | null) => {
  const urlWithoutHash = url.split('#')[0];
  switch (true) {
    case /^\/$/.test(urlWithoutHash):
      return undefined;
    case urlWithoutHash.includes(`/${LeasedSlugs.SALE}`):
      return 'buy';
    case urlWithoutHash.includes(`/${FilterSlugs.DAILY_RENTAL}`):
      return 'paid_daily';
    case urlWithoutHash.includes(`/${FilterSlugs.LONG_TERM_RENTAL}`) ||
      urlWithoutHash.includes(`/${LeasedSlugs.RENT}`):
      return 'rent';
    case url.includes(`/${LeasedSlugs.SALE}`) || (leased && leased !== 'true'):
      return 'buy';
    case leased === 'true':
      return undefined;
    default:
      return undefined;
  }
};

export const NewSearch = ({
  formElToAssign,
  isHomePage = false,
  refetchAllSearchQueries
}: NewSearchProps) => {
  const client = useApolloClient();
  const router = useRouter();
  const dispatch = useDispatch();
  const isClient = useClient();

  const formEl = useRef<{ reset: (values: SearchFormValues) => void } | null>(
    null
  );
  const { isSmall } = useScreenSize();
  const goBack = useGoBack();
  const { isSalePage, isRentPage, isItemsAllPage } = getSearchLandingPages(
    router.pathname,
    router.asPath
  );

  const storedFormState = useSelector(selectSearchFormCurrentState);
  const sortingValue = useSelector(selectSearchFormCurrentSortType);
  const currentCity: string = useSelector(selectSearchFormCurrentCity);
  const storedLocationIds: string[] = useSelector(selectSearchFormLocationIds);
  const { isVisible: isSideMenuOpen } = useSelector(selectSideMenuModalState);
  const categoryTouched = storedFormState?.category_touched || false;
  const [searchType, setSearchType] = useState<ReturnType<
    typeof getInitialSearchType
  > | null>(getInitialSearchType(router.asPath));
  const [hasAuthHash, setHasAuthHash] = useState(false);

  const resetCb = () => {
    const resetData = {
      city_id: Cities.Baku.id,
      leased: 'false',
      category_id: BUILDING_TYPE_ID.FLAT_ALL,
      location_ids: undefined,
      category_touched: false
    };
    formEl.current?.reset(resetData);
    dispatch(setChosenLocationNames([]));
    dispatch(setLocationIds([]));
    dispatch(setCurrentCity(Cities.Baku.id));
    dispatch(setCurrentState(resetData));
    dispatch(setAdriverSelectedCity(Cities.Baku.az));
    setSearchType(null);
  };

  useEffect(() => {
    setSearchType(getInitialSearchType(router.asPath, storedFormState?.leased));
    if (router.asPath === '/') {
      resetCb();
    }
  }, [router.pathname, router.asPath]);

  useEffect(() => {
    setHasAuthHash(router.asPath.includes(urlHashes.authentication));
  }, [router.asPath]);

  const handleReset = () => {
    if (!goBack()) return;

    router.beforePopState(({ url }) => {
      if (url === '/') {
        resetCb();
      }
      return true;
    });
  };

  const onSubmit = async (values: SearchFormValues) => {
    const { url, ...queryObj } = router.query;
    dispatch(setCurrentState(values));
    dispatch(setCategoryIds([values.category_id]));

    await client
      ?.query({
        query: getLocalDataQuery,
        variables: { id: values.city_id }
      })
      .then((localData: { data: LocalDataQuery }) => {
        const { href, as } = fromObjToUrl(
          values,
          localData.data,
          queryObj.sorting,
          false,
          queryObj.items_view
        );
        if (router.asPath === as) {
          window.scrollTo({ top: 0 });
          refetchAllSearchQueries?.();
        } else router.push(href, as);
      });
  };

  const changeFormHandler = ({ values }: { values: SearchFormValues }) => {
    const isEqual =
      JSON.stringify(
        convertSearchValues({
          city_id: ANY_CITY_ID,
          leased: 'false',
          category_id: BUILDING_TYPE_ID.FLAT_ALL
        })
      ) === JSON.stringify(convertValues(values));

    setTimeout(() => dispatch(setResetButtonIsDisabled(isEqual)), 0);
  };

  const initialValues: SearchFormValues = {
    city_id: currentCity || storedFormState?.city_id || ANY_CITY_ID,
    leased: String(searchType !== 'buy'),
    category_id: storedFormState?.category_id,
    paid_daily: searchType !== 'paid_daily' ? undefined : true,
    location_ids: storedLocationIds,
    sorting: sortingValue,
    ...storedFormState
  };

  const isCategoriesShown =
    isHomePage || isRentPage || isSalePage || isItemsAllPage;
  const isRentalBlockShown =
    !searchType || isSalePage || isRentPage || isItemsAllPage || isHomePage;
  const isAllItemsLinkShown = isHomePage && !isItemsAllPage;
  const isSearchTypeHeaderShown =
    isClient &&
    searchType &&
    isSmall &&
    !isHomePage &&
    !isItemsAllPage &&
    !isSideMenuOpen;

  return (
    <SearchContainer
      $isSearchPage={!isCategoriesShown}
      $hasAuthHash={hasAuthHash}
      {...(!hasAuthHash ? { 'data-lotriver-header': '' } : {})}
    >
      <Form
        onSubmit={onSubmit}
        subscription={{
          submitting: true,
          values: true
        }}
        initialValues={initialValues}
      >
        {(renderProps: FormRenderProps<SearchFormValues>) => {
          const { values } = renderProps;
          formEl.current = renderProps.form;
          const formElToAssignCurrent = formElToAssign;
          if (
            formElToAssignCurrent &&
            typeof formElToAssignCurrent !== 'function' &&
            formElToAssignCurrent.current
          ) {
            formElToAssignCurrent.current.reset = renderProps.form.reset;
          }
          const searchVariables = {
            filter: convertSearchValues(values)
          };

          return (
            <SearchBarForm
              method="get"
              id="new_search"
              acceptCharset="UTF-8"
              noValidate={true}
              onSubmit={renderProps.handleSubmit}
              isHomePage={isHomePage}
            >
              <FormSpy
                onChange={changeFormHandler}
                subscription={{ values: true }}
              />
              {isSearchTypeHeaderShown && (
                <SearchTypeHeader
                  values={values}
                  searchType={searchType}
                  handleBack={handleReset}
                  hasCategory={categoryTouched}
                />
              )}
              {isRentalBlockShown && <RentalTabs values={values} />}
              <NewSearchBar
                formEl={formEl}
                searchVariables={searchVariables}
                extraPadding={Boolean(!categoryTouched && searchType)}
              />
              <CategoriesContainer>
                {(!values.category_id || isCategoriesShown) && (
                  <NewSearchCategories asLinks />
                )}
                {isAllItemsLinkShown && <AllItemsLink />}
              </CategoriesContainer>
            </SearchBarForm>
          );
        }}
      </Form>
    </SearchContainer>
  );
};
